/**
 * Dialog "DELETE CONTRACT PLAN" — mirror Access form
 * `frmMatchingPSMainSubFormDelete` (file
 * `frontend/plan/access_all/form_frmMatchingPSMainSubFormDelete.txt`).
 *
 * Form chung cho cả Purchase (P) và Sale (S). VBA detect bằng pattern
 * `contract_no Like "*P-*"` / `"*S-*"`; FE truyền `side` prop tường minh để
 * khỏi parse string + tránh edge case (contract_no có thể mang ký tự khác
 * trong dataset thực).
 *
 *   ┌──── Read-only context ────┐
 *   │ Purchase/Sales Contract    │
 *   │ Supplier/Buyer            │
 *   └───────────────────────────┘
 *   ┌──── Tick to delete · Planed Quantity ────┐
 *   │ [☐] Instruction - Bảo Lộc      [   kg]   │
 *   │ [☐] Instruction - Nhơn Trạch   [   kg]   │
 *   │ [☐] Direct P-S                 [   kg]   │
 *   │                          [DELETE PLAN]    │
 *   └──────────────────────────────────────────┘
 *
 * VBA `btnDelete_Click`:
 *  - Validation: ít nhất 1 checkbox tick → MsgBox "Please tick to delete!"
 *  - Side P → UPDATE match_direct_ps WHERE p_side=… (Direct);
 *           UPDATE match_p_stock     WHERE p_side=… AND factory_name=… (BL/NT)
 *  - Side S → UPDATE match_direct_ps WHERE s_side=… (Direct);
 *           UPDATE match_s_stock     WHERE s_side=… AND factory_name=… (BL/NT)
 *  - is_status = 1 (soft-delete), update_date/updated_by track actor.
 */
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useState } from 'react'

import { useSnackbar } from '../../../hooks/useSnackbar'
import { titleCaseName } from '../../../utils/text'
import ConfirmDialog from '../../../components/ConfirmDialog'
import {
  type DoByMatch,
  useDeleteDoFromPlanMutation,
  useDeleteMatchPlanMutation,
  useLazyGetDosByContractQuery,
} from '../../../store/api/matchingPsApi'

type Side = 'P' | 'S'

const ACCENT: Record<Side, string> = {
  P: '#1e88e5',
  S: '#fb8c00',
}

export interface DeletePlanDialogProps {
  open: boolean
  onClose: () => void
  side: Side
  /** Row đang được chọn ở table (P hoặc S). */
  row: Record<string, unknown> | null
  /** Permission gating (Layer 4) — false → ẩn DELETE + show read-only banner. */
  canEdit?: boolean
}

function toNum(v: unknown): number {
  if (v == null || v === '') return 0
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}

function formatThousand(n: number): string {
  if (!Number.isFinite(n)) return ''
  return Math.round(n).toLocaleString('en-US')
}

/**
 * Outer wrapper — chỉ mount inner khi `open && row` để inner state reset tự
 * động khi đổi row (key=contractNo) hoặc reopen dialog.
 */
export default function DeletePlanDialog({ open, onClose, side, row, canEdit = true }: DeletePlanDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {open && row ? (
        <DeletePlanDialogContent
          key={`${side}-${String(row.contractNo ?? '')}`}
          side={side}
          row={row}
          onClose={onClose}
          canEdit={canEdit}
        />
      ) : null}
    </Dialog>
  )
}

interface DeletePlanDialogContentProps {
  side: Side
  row: Record<string, unknown>
  onClose: () => void
  canEdit: boolean
}

function DeletePlanDialogContent({ side, row, onClose, canEdit }: DeletePlanDialogContentProps) {
  const snack = useSnackbar()
  const accent = ACCENT[side]

  // Read-only fields lấy thẳng từ row. P/S dùng key khác nhau:
  // - P: pInstructBaoloc / pInstructNhontrach / pDirectS
  // - S: sInstructBaoloc / sInstructNhontrach / sDirectP
  const contractNo = String(row.contractNo ?? '')
  const cpName = String(row.cpName ?? '')
  const planedBL = side === 'P' ? toNum(row.pInstructBaoloc) : toNum(row.sInstructBaoloc)
  const planedNT =
    side === 'P' ? toNum(row.pInstructNhontrach) : toNum(row.sInstructNhontrach)
  const planedDirect = side === 'P' ? toNum(row.pDirectS) : toNum(row.sDirectP)

  // Danh sách DO đã book theo từng loại (note dưới mỗi tick). P-Instruction (P
  // side BL/NT) không tạo DO → để trống.
  const blLst = side === 'S' ? String(row.sInstructBaolocLst ?? '') : ''
  const ntLst = side === 'S' ? String(row.sInstructNhontrachLst ?? '') : ''
  const directLst = side === 'P' ? String(row.pDirectSLst ?? '') : String(row.sDirectPLst ?? '')

  const contractLabel = side === 'P' ? 'Purchase Contract' : 'Sales Contract'
  const partyLabel = side === 'P' ? 'Supplier' : 'Buyer'

  const [chbBL, setChbBL] = useState(false)
  const [chbNT, setChbNT] = useState(false)
  const [chbDirect, setChbDirect] = useState(false)

  const eligibleBL = planedBL > 0
  const eligibleNT = planedNT > 0
  const eligibleDirect = planedDirect > 0
  const hasEligible = eligibleBL || eligibleNT || eligibleDirect
  const allEligibleTicked =
    hasEligible &&
    (!eligibleBL || chbBL) &&
    (!eligibleNT || chbNT) &&
    (!eligibleDirect || chbDirect)

  const handleToggleAll = () => {
    const next = !allEligibleTicked
    setChbBL(eligibleBL && next)
    setChbNT(eligibleNT && next)
    setChbDirect(eligibleDirect && next)
  }

  const [del, { isLoading }] = useDeleteMatchPlanMutation()
  const [deleteDoRow, { isLoading: deletingDos }] = useDeleteDoFromPlanMutation()
  const [fetchDosByContract] = useLazyGetDosByContractQuery()
  // DO gắn Direct match của contract — confirm cho xoá hết DO trước khi xoá Direct plan.
  const [planDos, setPlanDos] = useState<DoByMatch[]>([])
  const [confirmDoOpen, setConfirmDoOpen] = useState(false)

  // Thực thi xoá: nếu có DO Direct → xoá hết DO trước rồi mới xoá plan.
  const performDelete = async () => {
    try {
      for (const d of planDos) {
        await deleteDoRow({ doId: d.deliveryOrderScsId }).unwrap()
      }
      const result = await del({
        contract_no: contractNo,
        side,
        delete_direct: chbDirect,
        delete_baoloc: chbBL,
        delete_nhontrach: chbNT,
      }).unwrap()
      const a = result.affected
      const summary = [
        chbDirect ? `Direct: ${a.direct}` : null,
        chbBL ? `BL: ${a.baoloc}` : null,
        chbNT ? `NT: ${a.nhontrach}` : null,
      ]
        .filter(Boolean)
        .join(', ')
      snack.success(`Successfully Deleted (${summary})`)
      setConfirmDoOpen(false)
      setPlanDos([])
      onClose()
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'data' in e
          ? String((e as { data?: { detail?: string } }).data?.detail ?? 'Delete failed.')
          : e instanceof Error
            ? e.message
            : 'Delete failed.'
      snack.error(msg)
    }
  }

  const handleDelete = async () => {
    if (!canEdit) {
      snack.error("You don't have permission for this action.")
      return
    }
    if (!chbBL && !chbNT && !chbDirect) {
      snack.warning('Please tick to delete!')
      return
    }
    // Bất kỳ loại nào tick có thể có DO → fetch & lọc theo đúng loại đang tick.
    if (chbDirect || chbBL || chbNT) {
      try {
        const dos = await fetchDosByContract({ contractNo, side }).unwrap()
        const relevant = dos.filter(
          (d) =>
            (chbDirect && d.kind === 'direct') ||
            (chbBL && d.kind === 'baoloc') ||
            (chbNT && d.kind === 'nhontrach'),
        )
        if (relevant.length > 0) {
          setPlanDos(relevant)
          setConfirmDoOpen(true)
          return
        }
        setPlanDos([])
      } catch {
        setPlanDos([])
      }
    } else {
      setPlanDos([])
    }
    await performDelete()
  }

  const readOnlyField = {
    size: 'small' as const,
    InputProps: { readOnly: true },
    InputLabelProps: { shrink: true },
    fullWidth: true,
  }

  return (
    <>
      <DialogTitle
        title="DELETE CONTRACT PLAN"
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: { xs: 0.75, sm: 1.25 },
          bgcolor: alpha(accent, 0.1),
          borderBottom: `2px solid ${alpha(accent, 0.55)}`,
          fontSize: { xs: '0.85rem', sm: '1rem' },
          fontWeight: 700,
          letterSpacing: 0.3,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        DELETE CONTRACT PLAN
      </DialogTitle>
      <DialogContent dividers sx={{ p: { xs: 1.25, sm: 2 } }}>
        {!canEdit ? (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Read-only — you don't have edit permission for this module.
          </Alert>
        ) : null}
        {/* Read-only context */}
        <Grid container spacing={1.25}>
          <Grid item xs={12} sm={6}>
            <TextField {...readOnlyField} label={contractLabel} value={contractNo} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField {...readOnlyField} label={partyLabel} value={titleCaseName(cpName)} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.4 }}
          >
            Tick to delete · Planed Quantity
          </Typography>
        </Divider>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={handleToggleAll}
            disabled={!canEdit || !hasEligible}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {allEligibleTicked ? 'Untick All' : 'Tick All'}
          </Button>
        </Box>

        {/* 3-row tick list. Cột phải hiện planed qty hiện tại (read-only) để
            user thấy số kg sắp soft-delete. */}
        <Stack spacing={1}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={8}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={chbBL}
                    onChange={(e) => setChbBL(e.target.checked)}
                    disabled={planedBL === 0}
                  />
                }
                label={
                  <Typography variant="body2">Instruction - Bảo Lộc</Typography>
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                {...readOnlyField}
                label="Planed Quantity"
                value={formatThousand(planedBL)}
              />
            </Grid>
          </Grid>
          {blLst ? (
            <Box sx={{ px: 1.25, pb: 0.75, bgcolor: (t) => alpha(t.palette.warning.main, 0.08) }}>
              <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 600 }}>
                Booked DO: <b>{blLst}</b> — will be deleted with this plan.
              </Typography>
            </Box>
          ) : null}

          <Grid container spacing={1} alignItems="center">
            <Grid item xs={8}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={chbNT}
                    onChange={(e) => setChbNT(e.target.checked)}
                    disabled={planedNT === 0}
                  />
                }
                label={
                  <Typography variant="body2">Instruction - Nhơn Trạch</Typography>
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                {...readOnlyField}
                label="Planed Quantity"
                value={formatThousand(planedNT)}
              />
            </Grid>
          </Grid>
          {ntLst ? (
            <Box sx={{ px: 1.25, pb: 0.75, bgcolor: (t) => alpha(t.palette.warning.main, 0.08) }}>
              <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 600 }}>
                Booked DO: <b>{ntLst}</b> — will be deleted with this plan.
              </Typography>
            </Box>
          ) : null}

          <Grid container spacing={1} alignItems="center">
            <Grid item xs={8}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={chbDirect}
                    onChange={(e) => setChbDirect(e.target.checked)}
                    disabled={planedDirect === 0}
                  />
                }
                label={<Typography variant="body2">Direct P-S</Typography>}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                {...readOnlyField}
                label="Planed Quantity"
                value={formatThousand(planedDirect)}
              />
            </Grid>
          </Grid>
          {directLst ? (
            <Box sx={{ px: 1.25, pb: 0.75, bgcolor: (t) => alpha(t.palette.warning.main, 0.08) }}>
              <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 600 }}>
                Booked DO: <b>{directLst}</b> — will be deleted with this plan.
              </Typography>
            </Box>
          ) : null}
        </Stack>

        {planedBL === 0 && planedNT === 0 && planedDirect === 0 ? (
          <Box
            sx={{
              mt: 2,
              p: 1,
              borderRadius: 1,
              bgcolor: (t) => alpha(t.palette.warning.main, 0.08),
              border: (t) => `1px dashed ${alpha(t.palette.warning.main, 0.4)}`,
            }}
          >
            <Typography variant="caption" color="warning.main">
              No active plan rows to delete for this contract.
            </Typography>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: { xs: 1.5, sm: 2 }, py: { xs: 0.75, sm: 1.25 } }}>
        <Button onClick={onClose} disabled={isLoading} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          disabled={!canEdit || isLoading || deletingDos || (!chbBL && !chbNT && !chbDirect)}
          variant="contained"
          color="error"
        >
          {isLoading || deletingDos ? 'Deleting…' : 'DELETE PLAN'}
        </Button>
      </DialogActions>

      {/* Direct P-S có DO đã book → confirm xoá hết DO trước rồi mới xoá plan. */}
      <ConfirmDialog
        open={confirmDoOpen}
        title="Delete Plan"
        message={
          planDos.length > 0
            ? `This contract's Direct P-S has ${planDos.length} booked DO: ${planDos
                .map((d) => d.doNo || `#${d.deliveryOrderScsId}`)
                .join(', ')}. Deleting will also delete these DO(s). Continue?`
            : ''
        }
        confirmLabel="Delete DO(s) + Plan"
        danger
        loading={isLoading || deletingDos}
        onConfirm={performDelete}
        onClose={() => setConfirmDoOpen(false)}
      />
    </>
  )
}
